<template>
  <el-dialog
    :visible.sync="visible"
    title="查看线上执法详情"
    width="600px"
    :close-on-click-modal="false"
  >
    <div class="new-hearing-dialog">
      <!-- 标题区 -->
      <div class="title-container">
        <span class="title-icon-wrapper">
          <span class="title-icon"></span>
        </span>
        <h2 class="title">查看线上执法详情</h2>
      </div>
      
      <div class="hearing-form">
        <!-- 开庭时间（实际开庭时间） -->
        <div class="form-item">
          <label class="form-label">开始时间（实际开始时间）</label>
          <el-input 
            :value="getHearingRealStartTime()" 
            placeholder="暂无实际开始时间"
            readonly
            class="readonly-input"
          />
        </div>

        <!-- 闭庭时间（实际闭庭时间） -->
        <div class="form-item">
          <label class="form-label">结束时间（实际结束时间）</label>
          <el-input 
            :value="getHearingRealEndTime()" 
            placeholder="暂无实际结束时间"
            readonly
            class="readonly-input"
          />
        </div>

        <!-- 线上执法室 -->
        <div class="form-item">
          <label class="form-label">线上执法室</label>
          <el-input 
            :value="getCourtName()" 
            placeholder="暂无执法室信息"
            readonly
            class="readonly-input"
          />
        </div>

        <!-- 参会人信息区域 -->
        <div class="participant-section">
          <div class="section-title">参会人信息：</div>

          <!-- 按顺序显示参会人：申请人、被申请人、代理人 -->
          <div v-for="participant in sortedParticipants" :key="participant.participantId" class="info-text">
            <span class="identity">{{ getIdentityText(participant.participantIdentity) }}</span>
            <span class="name">{{ participant.participantName }}</span>
            <span v-if="participant.meetingNumber" class="meeting-number">会议号: {{ participant.meetingNumber }}</span>
          </div>
        </div>

        <div class="action-buttons">
          <el-button @click="handleClose" size="medium">关闭</el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import request from '@/utils/request' // 引入请求工具

export default {
  name: 'HearingDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    hearingData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      courtName: '', // 存储查询到的仲裁庭名称
      participants: [] // 存储参会人信息
    }
  },
  computed: {
    // 按身份顺序排序的参会人列表：申请人(1) -> 被申请人(2) -> 代理人(3)
    sortedParticipants() {
      return [...this.participants].sort((a, b) => {
        return a.participantIdentity - b.participantIdentity;
      });
    }
  },
  watch: {
    hearingData: {
      handler(newVal) {
        if (newVal) {
          // 查询仲裁庭名称
          if (newVal.courtId) {
            this.fetchCourtName();
          }
          // 查询参会人信息
          if (newVal.hearingId) {
            this.fetchParticipants();
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 获取实际开庭时间
    getHearingRealStartTime() {
      if (!this.hearingData) return '';
      return this.hearingData.hearingRealStartTime || this.hearingData.hearing_real_start_time || '';
    },
    
    // 获取实际闭庭时间
    getHearingRealEndTime() {
      if (!this.hearingData) return '';
      return this.hearingData.hearingRealEndTime || this.hearingData.hearing_real_end_time || '';
    },
    
    // 获取仲裁庭名称
    getCourtName() {
      if (!this.hearingData) return '';
      
      // 如果有具体的仲裁庭名称，直接显示
      if (this.hearingData.courtName) {
        return this.hearingData.courtName;
      }
      
      // 如果有查询到的仲裁庭名称，显示查询结果
      if (this.courtName) {
        return this.courtName;
      }
      
      // 如果有仲裁庭ID但还没查询到名称，显示加载状态
      if (this.hearingData.court_id || this.hearingData.courtId) {
        return '正在查询执法室信息...';
      }
      
      // 如果都没有，返回空字符串
      return '';
    },
    
    // 根据court_id查询虚拟仲裁庭名称
    async fetchCourtName() {
      try {
        const courtId = this.hearingData.courtId;
        if (!courtId) return;
        
        // 输出查询的courtId
        console.log('正在查询仲裁庭信息，courtId:', courtId);
        
        // 调用后端接口查询仲裁庭信息
        const response = await request.get(`/api/virtualCourt/${courtId}`);
        if (response && response.data && response.data.name) {
          this.courtName = response.data.name;
        } else {
          // 如果查询失败，显示ID
          this.courtName = `执法室 (ID: ${courtId})`;
        }
      } catch (error) {
        console.error('查询仲裁庭信息失败:', error);
        // 查询失败时显示ID
        const courtId = this.hearingData.courtId;
        this.courtName = `线上执法室 (ID: ${courtId})`;
      }
    },
    
    // 根据hearingId查询参会人信息
    async fetchParticipants() {
      try {
        const hearingId = this.hearingData.hearingId;
        if (!hearingId) return;
        
        // 输出查询的hearingId
        console.log('正在查询参会人信息，hearingId:', hearingId);
        
        // 调用正确的后端接口查询参会人信息
        const response = await request.get(`/api/case-hearing-participant/byHearing/${hearingId}`);
        if (response && response.data) {
          this.participants = response.data;
          console.log('查询到的参会人信息:', this.participants);
          
          // 调试：查看每个参会人的详细信息
          this.participants.forEach((participant, index) => {
            console.log(`参会人${index + 1}:`, {
              id: participant.id,
              participantIdentity: participant.participantIdentity,
              name: participant.name,
              meetingNumber: participant.meetingNumber,
              fullData: participant
            });
          });
        } else {
          this.participants = [];
        }
      } catch (error) {
        console.error('查询参会人信息失败:', error);
        this.participants = [];
      }
    },
    
        // 根据身份标识获取身份文本
    getIdentityText(identity) {
      const identityMap = {
        1: '申请人',
        2: '被申请人', 
        3: '代理人',
        4: '执法员',
        5: '书记员'
      };
      return identityMap[identity] || '未知身份';
    },
    
    // 关闭弹窗
    handleClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
/* 庭审详情模态框样式 - 套用 NewHearing 的样式 */
.new-hearing-dialog {
  padding: 20px;
}

.title-container {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7ed;
}

.title-icon-wrapper {
  display: inline-block;
  width: 4px;
  height: 20px;
  background-color: #409eff;
  margin-right: 12px;
  border-radius: 2px;
}

.title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.hearing-form {
  margin-top: 20px;
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
}

.readonly-input {
  background-color: #f5f7fa;
}

.readonly-input >>> .el-input__inner {
  background-color: #f5f7fa;
  color: #606266;
  cursor: not-allowed;
}

.participant-section {
  margin: 24px 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.info-text {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.identity {
  font-weight: 500;
  color: #606266;
  min-width: 80px;
  flex-shrink: 0;
}

.name {
  color: #303133;
  margin: 0 20px;
  flex-shrink: 0;
  min-width: 80px;
}

.meeting-number {
  color: #409eff;
  font-size: 13px;
  margin-left: 20px;
}

.action-buttons {
  margin-top: 24px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
}
</style>
